import * as Snackbar from '@synergy/shared/snackbar/data-access';
import { Dispatch } from 'redux';
import { PoolNotification } from '@synergy/shared/snackbar/data-access';
import { hasErrors } from '@synergy/utils';

import { paloMissingFieldsMessage } from '../../../../../common/components/palo/palo-missing-fields-message';
import { Api } from '../../../../../api';
import { AppState } from '../../../../../store/models/app-state';
import { Demand } from '../../../models/demand';
import { saveOverview } from './overview/demand-detail-overview-actions';
import { saveParticipants } from './participants/demand-detail-participants-actions';
import { savePositions } from './positions/demand-detail-positions-actions';
import { saveTopics } from './topics/demand-detail-topics-actions';

export const DEMAND_DETAIL_UNLOCK_TOGGLE = 'DEMAND_DETAIL_UNLOCK_TOGGLE';
export const DEMAND_DETAIL_RESET = 'DEMAND_DETAIL_RESET';
export const DEMAND_DETAIL_STARTED = 'DEMAND_DETAIL_STARTED';
export const DEMAND_DETAIL_COMPLETED = 'DEMAND_DETAIL_COMPLETED';
export const DEMAND_DETAIL_SCHEDULED = 'DEMAND_DETAIL_SCHEDULED';
export const DEMAND_DETAIL_SAVE_ALL = 'DEMAND_DETAIL_SAVE_ALL';
export const DEMAND_DETAIL_TRANSITION_DATA_RECEIVED = 'DEMAND_DETAIL_TRANSITION_DATA_RECEIVED';
export const DEMAND_DETAIL_TOTAL_QUANTITY_POSITIONS_RECEIVED = 'DEMAND_DETAIL_TOTAL_QUANTITY_POSITIONS_RECEIVED';

export const unlockToggle = () => ({
    type: DEMAND_DETAIL_UNLOCK_TOGGLE,
});

export const Reset = () => ({
    type: DEMAND_DETAIL_RESET,
});

export const deleteDemand = () => {
    return async (dispatch: Dispatch, getState: () => AppState, { api }: { api: Api }) => {
        const demandId = getState().demandDetail.overview.stepData.values.id;
        if (!demandId) {
            return;
        }

        const response = await api.demands.delete(demandId);
        if (response.success === false) {
            dispatch(Snackbar.enqueue(PoolNotification.error('app.snackbar.error')));
        }
    };
};

export const startDemand = () => {
    return async (dispatch: Dispatch, getState: () => AppState, { api }: { api: Api }) => {
        const demandId = getState().demandDetail.overview.stepData.values.id;
        if (!demandId) {
            return;
        }

        const response = await api.demands.start(demandId);
        if (response.success === true) {
            dispatch({ type: DEMAND_DETAIL_STARTED });
        } else {
            dispatch(Snackbar.enqueue(PoolNotification.error('app.snackbar.error')));
        }
    };
};

export const scheduleDemand = () => {
    return async (dispatch: Dispatch, getState: () => AppState, { api }: { api: Api }) => {
        const demandId = getState().demandDetail.overview.stepData.values.id;
        if (!demandId) {
            return;
        }

        const response = await api.demands.schedule(demandId);
        if (response.success === true) {
            dispatch({ type: DEMAND_DETAIL_SCHEDULED });
        } else {
            dispatch(Snackbar.enqueue(PoolNotification.error('app.snackbar.error')));
        }
    };
};

export const completeDemand = () => {
    return async (dispatch: Dispatch, getState: () => AppState, { api }: { api: Api }) => {
        const demandId = getState().demandDetail.overview.stepData.values.id;
        if (!demandId) {
            return;
        }

        const response = await api.demands.complete(demandId);
        if (response.success === true) {
            dispatch({ type: DEMAND_DETAIL_COMPLETED });
        } else {
            dispatch(Snackbar.enqueue(PoolNotification.error('app.snackbar.error')));
        }
    };
};

export const saveAll = () => {
    return async (dispatch: Dispatch, getState: () => AppState, { api }: { api: Api }) => {
        const state = getState().demandDetail;

        dispatch({ type: DEMAND_DETAIL_SAVE_ALL });

        if (
            hasErrors(state.overview.stepData.errors) ||
            hasErrors(state.positions.stepData.errors) ||
            hasErrors(state.topics.stepData.errors) ||
            hasErrors(state.participants.stepData.errors)
        ) {
            // Which fields, and on which step — "check the form" sends
            // people looking, and the step holding them need not be the one
            // on screen (#2206, R-134).
            const missing = paloMissingFieldsMessage([
                { tab: 'app.demands.step.overview', errors: state.overview.stepData.errors },
                { tab: 'app.demands.step.positions', errors: state.positions.stepData.errors },
                { tab: 'app.demands.step.topics', errors: state.topics.stepData.errors },
                { tab: 'app.demands.step.participants', errors: state.participants.stepData.errors },
            ]);

            dispatch(
                Snackbar.enqueue(
                    missing
                        ? PoolNotification.error('app.snackbar.formErrorsNamed', { fields: missing })
                        : PoolNotification.error('app.snackbar.validationErrors')
                )
            );

            return false;
        }

        if (state.overview.stepData.isDirty) {
            if (!(await saveOverview()(dispatch, getState, { api }))) {
                dispatch(Snackbar.enqueue(PoolNotification.error('app.snackbar.demandSavedError')));
                return false;
            }
        }

        if (state.positions.stepData.isDirty) {
            if (!(await savePositions()(dispatch, getState, { api }))) {
                dispatch(Snackbar.enqueue(PoolNotification.error('app.snackbar.demandSavedError')));
                return false;
            }
        }

        if (state.topics.stepData.isDirty) {
            if (!(await saveTopics()(dispatch, getState, { api }))) {
                dispatch(Snackbar.enqueue(PoolNotification.error('app.snackbar.demandSavedError')));
                return false;
            }
        }

        if (state.participants.stepData.isDirty) {
            if (!(await saveParticipants()(dispatch, getState, { api }))) {
                dispatch(Snackbar.enqueue(PoolNotification.error('app.snackbar.demandSavedError')));
                return false;
            }
        }

        dispatch(Snackbar.enqueue(PoolNotification.success('app.snackbar.saved')));
        return true;
    };
};

export const fetchDemandTransitionData = (id?: number) => {
    return async (dispatch: Dispatch, getState: () => AppState, { api }: { api: Api }) => {
        const demandId = id ?? getState().demandDetail.overview.stepData.values.id;
        if (!demandId) {
            return;
        }

        const response = await api.demands.getTransitionData(demandId);
        if (response.success === true) {
            dispatch({
                type: DEMAND_DETAIL_TRANSITION_DATA_RECEIVED,
                transitionData: Demand.fromPlainObject(response.result),
            });
        } else {
            dispatch(Snackbar.enqueue(PoolNotification.error('app.snackbar.error')));
        }
    };
};

export const fetchTotalQuantityPositions = () => {
    return async (dispatch: Dispatch, getState: () => AppState, { api }: { api: Api }) => {
        const demandId = getState().demandDetail.overview.stepData.values.id;
        if (!demandId) {
            return;
        }

        const response = await api.demands.getTotalQuantityDemandPositions(demandId);
        if (response.success === true) {
            dispatch({
                type: DEMAND_DETAIL_TOTAL_QUANTITY_POSITIONS_RECEIVED,
                totalQuantity: response.result.demandItems,
            });
        } else {
            dispatch(Snackbar.enqueue(PoolNotification.error('app.snackbar.error')));
        }
    };
};
